// The logo beats once each time continuous monitoring completes a pass, so a
// user watching the screen can tell the numbers in front of them are fresh.
// A window event keeps the emitter (provider-result polling) and the listener (the
// top bar logo) independent of each other's render tree.
export const MONITORING_PULSE_EVENT = 'oomero:monitoring-pulse';

// Roughly two heartbeats. Kept here so the animation and the timer agree.
export const MONITORING_PULSE_DURATION_MS = 1800;

export const createMonitoringResultObserver = (emit = emitMonitoringPulse) => {
  let previous;
  return (completedAt) => {
    const timestamp = completedAt ? Date.parse(completedAt) : NaN;
    if (!Number.isFinite(timestamp)) return;
    if (previous !== undefined && timestamp > previous) emit({ completedAt });
    previous = Math.max(previous ?? timestamp, timestamp);
  };
};

export const emitMonitoringPulse = (detail = {}) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(MONITORING_PULSE_EVENT, { detail }));
};

export const subscribeToMonitoringPulse = (handler) => {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(MONITORING_PULSE_EVENT, handler);
  return () => window.removeEventListener(MONITORING_PULSE_EVENT, handler);
};

// Open (unprocessed) alerts turn the Oomero Pulse navigation item red. The top bar
// already polls monitoring status, so it publishes the count and the sidebar listens.
export const OPEN_ALERTS_EVENT = 'oomero:open-alerts';
let lastOpenAlerts = null;

export const publishOpenAlerts = (count) => {
  const value = Number.isFinite(Number(count)) && count !== null ? Number(count) : null;
  if (value === lastOpenAlerts) return;
  lastOpenAlerts = value;
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_ALERTS_EVENT, { detail: { count: value } }));
};

export const getOpenAlerts = () => lastOpenAlerts;

export const subscribeToOpenAlerts = (handler) => {
  if (typeof window === 'undefined') return () => {};
  const listener = (event) => handler(event.detail?.count ?? null);
  window.addEventListener(OPEN_ALERTS_EVENT, listener);
  return () => window.removeEventListener(OPEN_ALERTS_EVENT, listener);
};

// Re-reads the open-alert count right after an alert is answered (facial duplicate reviewed,
// IDV overridden, KYC mismatch accepted, alert marked reviewed) so the navigation indicator
// and the counts drop at once instead of on the next 60-second poll.
export const refreshOpenAlerts = async (client) => {
  try {
    const api = client || (await import('./api')).default;
    const response = await api.get('/dashboard/monitoringStatus');
    const count = response?.data?.data?.open_alerts;
    publishOpenAlerts(count);
    return count ?? null;
  } catch (_) {
    // A failed re-read leaves the last known count in place; the poll corrects it.
    return null;
  }
};
